(() => {
  if (window.fragrifyCartEnhancement) return;
  window.fragrifyCartEnhancement = true;
  let busy = false;
  let currentCodes = [];
  let discountPending = false;
  let generation = 0;
  let pendingFocus = null;
  const root = () => document.querySelector('[data-cart-section]');
  const config = () => {
    try { return JSON.parse(root()?.querySelector('[data-cart-config]')?.textContent || '{}'); }
    catch { return {}; }
  };
  const announce = (message, discount = false) => {
    const target = root()?.querySelector(discount ? '[data-discount-status]' : '[data-cart-feedback]');
    if (target) target.textContent = message;
  };
  function setBusy(value) {
    busy = value;
    root()?.setAttribute('aria-busy', String(value));
    root()?.querySelectorAll('button, input, textarea').forEach((element) => {
      if (value) {
        element.dataset.cartWasDisabled = String(element.disabled);
        element.disabled = true;
      } else if (element.dataset.cartWasDisabled !== undefined) {
        element.disabled = element.dataset.cartWasDisabled === 'true';
        delete element.dataset.cartWasDisabled;
      }
    });
  }
  function renderCodes(cart) {
    const section = root();
    if (!section || !Array.isArray(cart.discount_codes)) return;
    currentCodes = cart.discount_codes.filter((entry) => typeof entry.code === 'string');
    const list = section.querySelector('[data-discount-codes]');
    if (!list) return;
    list.replaceChildren();
    const strings = config().strings;
    for (const entry of currentCodes) {
      const li = document.createElement('li');
      const label = document.createElement('span');
      label.textContent = `${entry.code} — ${entry.applicable === true ? strings.discountActive : strings.discountInactive}`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'fgk-link';
      remove.dataset.discountRemove = entry.code;
      remove.textContent = strings.discountRemove;
      remove.setAttribute('aria-label', `${strings.discountRemove}: ${entry.code}`);
      li.append(label, remove);
      list.append(li);
    }
    const clear = section.querySelector('[data-discount-clear]');
    if (clear) clear.hidden = currentCodes.length === 0;
  }
  async function request(url, data) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });
    let cart;
    try { cart = await response.json(); }
    catch { throw new Error(config().strings.error); }
    if (!response.ok) throw new Error(cart.description || cart.message || config().strings.error);
    return cart;
  }
  async function refresh(cart, cfg = config()) {
    let markup = cart.sections?.[cfg.sectionId];
    if (!markup) {
      const url = new URL(cfg.cartUrl, location.origin);
      url.searchParams.set('sections', cfg.sectionId);
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(cfg.strings.error);
      markup = (await response.json())[cfg.sectionId];
    }
    const replacement = new DOMParser().parseFromString(markup || '', 'text/html').querySelector('[data-cart-section]');
    if (!replacement) throw new Error(cfg.strings.error);
    const previous = root();
    const active = pendingFocus || document.activeElement;
    const noteField = previous?.querySelector('textarea[name="note"]');
    const draftNote = noteField?.value;
    const noteOpen = noteField?.closest('details')?.open;
    const lineIndex = active?.closest('[data-cart-line]')?.querySelector('[data-qty-field]')?.dataset.lineIndex;
    const kind = active?.matches('[data-promo-input], [data-promo-apply], [data-discount-remove], [data-discount-clear]') ? 'promo' : active?.matches('textarea[name="note"], [data-cart-note-save]') ? 'note' : null;
    const scroll = window.scrollY;
    previous.replaceWith(replacement);
    const newNote = replacement.querySelector('textarea[name="note"]');
    if (newNote && draftNote !== undefined) {
      newNote.value = draftNote;
      if (noteOpen) newNote.closest('details').open = true;
    }
    if (kind === 'promo') replacement.querySelector('[data-promo-input]')?.focus({ preventScroll: true });
    else if (kind === 'note') replacement.querySelector('textarea[name="note"]')?.focus({ preventScroll: true });
    else if (lineIndex) {
      const next = replacement.querySelector(`[data-line-index="${lineIndex}"]`) || replacement.querySelector('[data-qty-field]') || replacement.querySelector('.fgk-empty__cta');
      next?.focus({ preventScroll: true });
    }
    window.scrollTo({ top: scroll, behavior: 'instant' });
    renderCodes(cart);
    loadRecommendations();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'cart-section' } }));
  }
  async function mutate(payload, endpoint, message, discount = false) {
    if (busy) return null;
    const cfg = config();
    const note = root()?.querySelector('textarea[name="note"]')?.value;
    if (note !== undefined && endpoint === cfg.updateUrl) payload.note = note;
    pendingFocus = pendingFocus || document.activeElement;
    setBusy(true);
    announce(cfg.strings.updating, discount);
    try {
      const cart = await request(endpoint, { ...payload, sections: [cfg.sectionId], sections_url: cfg.cartUrl });
      await refresh(cart, cfg);
      announce(typeof message === 'function' ? message(cart) : message || cfg.strings.updated, discount);
      return cart;
    } catch (error) {
      announce(error.message || cfg.strings.error, discount);
      return null;
    } finally {
      setBusy(false);
      if (pendingFocus?.isConnected) pendingFocus.focus({ preventScroll: true });
      pendingFocus = null;
    }
  }
  async function applyDiscount(removeCode, clear = false) {
    if (busy || discountPending) return;
    const cfg = config();
    const input = root()?.querySelector('[data-promo-input]');
    const entered = (input?.value || '').split(',').map((code) => code.trim()).filter(Boolean);
    if (!clear && !removeCode && entered.length === 0) {
      announce(cfg.strings.discountEmpty, true);
      input?.focus();
      return;
    }
    discountPending = true;
    pendingFocus = document.activeElement;
    setBusy(true);
    announce(cfg.strings.updating, true);
    try {
      const response = await fetch(`${cfg.cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(cfg.strings.error);
      renderCodes(await response.json());
    } catch {
      discountPending = false;
      setBusy(false);
      pendingFocus?.focus();
      pendingFocus = null;
      announce(cfg.strings.error, true);
      return;
    }
    setBusy(false);
    const codes = clear ? [] : currentCodes.map((entry) => entry.code).filter((code) => code !== removeCode);
    if (!removeCode && !clear) codes.push(...entered);
    const uniqueCodes = codes.filter((code, index, all) => all.findIndex((other) => other.toLowerCase() === code.toLowerCase()) === index);
    try { await mutate({ discount: uniqueCodes.join(',') }, cfg.updateUrl, (cart) => {
      if (clear || removeCode) return cfg.strings.discountRemoved;
      if (!Array.isArray(cart.discount_codes)) return cfg.strings.discountUnknown;
      const applied = entered.every((code) => cart.discount_codes.some((entry) => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable === true));
      return applied ? cfg.strings.discountApplied : cfg.strings.discountIneligible;
    }, true); } finally { discountPending = false; }
  }
  async function changeQuantity(field, quantity) {
    if (!field || busy) return;
    if (!Number.isInteger(quantity) || quantity < 0) {
      field.reportValidity();
      return;
    }
    await mutate({ id: field.dataset.lineKey, quantity }, config().changeUrl);
  }
  async function loadRecommendations() {
    const section = root();
    const cfg = config();
    const container = section?.querySelector('[data-cart-recommendations]');
    const grid = container?.querySelector('[data-cart-recommendations-grid]');
    if (!grid || grid.children.length >= 4) return;
    const token = ++generation;
    const excluded = new Set((cfg.productIds || []).map(String));
    grid.querySelectorAll('[data-merch-product-id]').forEach((card) => excluded.add(card.dataset.merchProductId));
    for (const intent of ['complementary', 'related']) {
      for (const productId of (cfg.productIds || []).slice(0, 4)) {
        if (token !== generation || grid.children.length >= 4) return;
        try {
          const url = new URL(cfg.recommendationsUrl, location.origin);
          url.searchParams.set('section_id', 'product-recommendations');
          url.searchParams.set('product_id', productId);
          url.searchParams.set('limit', '10');
          url.searchParams.set('intent', intent);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (token !== generation || !grid.isConnected) return;
          const cards = html.querySelectorAll('[data-merch-product-id]');
          for (const card of cards) {
            const id = card.dataset.merchProductId;
            if (excluded.has(id) || card.dataset.merchAvailable === 'false') continue;
            if (grid.children.length >= 4) break;
            if (grid.children.length === 0) container.querySelector('h2').textContent = intent === 'complementary' ? cfg.strings.complementaryHeading : cfg.strings.relatedHeading;
            excluded.add(id);
            grid.append(card);
            container.hidden = false;
          }
        } catch {}
      }
    }
  }
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-section]')) return;
    const step = event.target.closest('[data-qty-step]');
    const remove = event.target.closest('[data-cart-remove]');
    const discountRemove = event.target.closest('[data-discount-remove]');
    if (step) {
      event.preventDefault();
      const field = step.closest('.fgk-qty').querySelector('[data-qty-field]');
      const quantity = Math.max(0, Number(field.value) + Number(step.dataset.qtyStep) * (Number(field.step) || 1));
      changeQuantity(field, quantity);
    } else if (remove) {
      event.preventDefault();
      changeQuantity(remove.closest('[data-cart-line]').querySelector('[data-qty-field]'), 0);
    } else if (discountRemove) {
      applyDiscount(discountRemove.dataset.discountRemove);
    }
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-cart-section] [data-qty-field]')) changeQuantity(event.target, Number(event.target.value));
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-cart-discount-form]')) {
      event.preventDefault();
      applyDiscount();
    } else if (form.matches('[data-cart-discount-reset]')) {
      event.preventDefault();
      applyDiscount(null, true);
    } else if (form.matches('[data-cart-form]') && event.submitter?.matches('[data-cart-note-save]')) {
      event.preventDefault();
      mutate({ note: form.querySelector('[name="note"]').value }, config().updateUrl, config().strings.noteSaved);
    } else if (form.matches('[data-cart-form]')) {
      if (busy) { event.preventDefault(); return; }
      form.setAttribute('aria-busy', 'true');
    }
  });
  document.addEventListener('cart:updated', async (event) => {
    if (!root() || busy || event.detail?.source === 'cart-section') return;
    if (event.detail?.cart) {
      try { await refresh(event.detail.cart); } catch { announce(config().strings.error); }
    }
  });
  document.addEventListener('shopify:section:load', initialize);
  async function initialize() {
    if (!root()) return;
    root().querySelector('[data-discount-clear]')?.setAttribute('hidden', '');
    loadRecommendations();
    try {
      const response = await fetch(`${config().cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) renderCodes(await response.json());
    } catch {}
  }
  initialize();
})();
